import { useState, useEffect, useMemo } from "react";
import Base from "@/components/base";
import Container from "@/components/container";
import { useNavigate } from "react-router-dom";
import getDistance from "geolib/es/getDistance";
import {
  useGetCharacteristicsQuery,
  useGetVehicleInfoQuery,
  useGetVehicleLocationQuery,
  VehicleModel,
} from "@/generated/graphql";
import { DateTime } from "luxon";
import { getVehicleData } from "@/util/vehicle";

const Home = () => {
  const datetime = useMemo(() => DateTime.now(), []);
  const [{ data: characteristics, fetching: characteristicsFetching }] =
    useGetCharacteristicsQuery({
      variables: { timestamp: datetime },
    });
  const [{ data: vehicle, fetching: infoFetching }] = useGetVehicleInfoQuery();

  const isFetching = characteristicsFetching || infoFetching;

  const vehicleData = useMemo(() => {
    if (!characteristics || !vehicle?.currentEndUser.vehicle?.model) return {};
    return getVehicleData(
      vehicle.currentEndUser.vehicle.model,
      characteristics,
    );
  }, [characteristics, vehicle]);

  return (
    <Base>
      <Container loading={isFetching}>
        <Capacity
          stateOfCharge={vehicleData.stateOfCharge}
          model={vehicle?.currentEndUser.vehicle?.model}
        />
        <MapComponent />
        <VehicleData
          estimatedDistance={vehicleData.estimatedDistance}
          estimatedDailyDistance={vehicleData.estimatedDailyDistance}
        />
      </Container>
    </Base>
  );
};

const PartialCircleProgress = ({ percentage }: { percentage: number }) => {
  // progress
  const strokeDashoffset = (percentage / 100) * 70;

  return (
    <div className="relative size-52">
      <svg
        className="rotate-[144deg] size-full"
        viewBox="0 0 36 36"
        xmlns="http://www.w3.org/2000/svg"
      >
        <circle
          cx="18"
          cy="18"
          r="16"
          fill="none"
          className="stroke-current text-[#C3E2C380]"
          strokeWidth="3"
          strokeDasharray="70 100"
          strokeLinecap="butt"
        ></circle>
        <circle
          cx="18"
          cy="18"
          r="16"
          fill="none"
          className="stroke-current text-white/40"
          strokeWidth="2"
          strokeDasharray="70 100"
          strokeLinecap="butt"
        ></circle>
        <circle
          cx="18"
          cy="18"
          r="16"
          fill="none"
          className="stroke-current text-white/60"
          strokeWidth="1"
          strokeDasharray="70 100"
          strokeLinecap="butt"
        ></circle>

        <circle
          cx="18"
          cy="18"
          r="16"
          fill="none"
          className="stroke-current text-brand"
          strokeWidth="2"
          strokeDasharray={`${strokeDashoffset} 100`}
          strokeLinecap="butt"
        ></circle>
        <circle
          cx="18"
          cy="18"
          r="16"
          fill="none"
          className="stroke-current text-brand-light/50"
          strokeWidth="3"
          strokeDasharray="70 100"
          strokeLinecap="butt"
        ></circle>
        <circle
          cx="18"
          cy="18"
          r="16"
          fill="none"
          className="stroke-current text-brand"
          strokeWidth="1"
          strokeDasharray={`${strokeDashoffset} 100`}
          strokeLinecap="butt"
        ></circle>
      </svg>

      <div className="absolute top-0 start-1/2 transform -translate-x-1/2 translate-y-[140%] text-center flex flex-col items-center">
        <span className="text-5xl font-bold text-[#4B4B4B]">
          {percentage.toFixed(0)}
          <span className="text-sm font-bold">%</span>
        </span>
      </div>
    </div>
  );
};

const Capacity = ({
  stateOfCharge,
  model,
}: {
  stateOfCharge?: number;
  model: VehicleModel | undefined;
}) => {
  var modelImage = "";

  switch (model) {
    case VehicleModel.TukTuk_5Seats:
      modelImage = "/tuktuk.png";
      break;
    case VehicleModel.W2500GreyVintage:
      modelImage = "/motorbike.png";
      break;
    case VehicleModel.W2500Red:
      modelImage = "/motorbike.png";
      break;
    case VehicleModel.Unknown:
      modelImage = "/tuktuk.png";
      break;
    case VehicleModel.TukTuk:
      modelImage = "/tuktuk.png";
      break;
    case VehicleModel.Motorbike:
      modelImage = "/motorbike.png";
      break;
    case VehicleModel.Scooter:
      modelImage = "/scooter.png";
      break;
    default:
      modelImage = "/tuktuk.png";
  }

  return (
    <div className="flex flex-col items-center">
      <PartialCircleProgress percentage={stateOfCharge || 0} />
      <img
        src={modelImage}
        alt="Epsilon Tuktuk"
        className=" w-[136px] h-auto -mt-12"
      />
    </div>
  );
};

const MapComponent = () => {
  const navigate = useNavigate();
  const [distance, setDistance] = useState<number | null>(null);
  const [userLocation, setUserLocation] = useState<{
    latitude: number;
    longitude: number;
  } | null>(null);

  const [{ data: vehicleLocation, fetching: loading }] =
    useGetVehicleLocationQuery();

  useEffect(() => {
    if (!userLocation) return;
    if (!vehicleLocation) return;
    const vehicleLoc =
      vehicleLocation?.currentEndUser.characteristics.deviceCharacteristics
        .geolocation.current;
    if (!vehicleLoc) return;
    const distance = getDistance(userLocation, vehicleLoc);
    setDistance(distance);
  }, [userLocation, vehicleLocation]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          });
        },
        (error) => {
          console.error("Error obtaining location:", error);
        },
      );
    } else {
      console.error("Geolocation is not supported by your browser");
    }
  }, []);

  return (
    <div>
      <button
        onClick={() => navigate("/map")}
        className="w-full h-[40px] rounded-md overflow-hidden relative bg-cover bg-center bg-no-repeat flex justify-between px-3 py-3 bg-opacity-75"
        style={{
          backgroundImage: `url(/map-bg.png)`,
          backgroundColor: "#FFFFFF90",
          backgroundBlendMode: "lighten",
        }}
      >
        <span className="text-[#4B4B4B] text-[10px]">Find my</span>
        <div className="shadow-md shadow-black/20 z-50 bg-brand/90 h-4 px-1.5 rounded-full flex items-center justify-center">
          <span className="text-white text-[10px] font-normal block">
            {loading || distance === null ? "" : `${distance}km`}
          </span>
        </div>
      </button>
    </div>
  );
};

const VehicleData = ({
  estimatedDistance,
  estimatedDailyDistance,
}: {
  estimatedDistance?: string;
  estimatedDailyDistance?: string;
}) => {
  return (
    <div className="w-full flex flex-grow gap-7 my-6">
      <div className="bg-white border border-[#F1FBF4] flex-1 p-2 space-y-2 rounded-md">
        <p className="text-sm text-brand font-medium">Distance</p>
        <p className="text-[#575757] text-[10px] font-light">
          {estimatedDistance ? `${estimatedDistance}km` : "-"}
        </p>
      </div>
      <div className="bg-white border border-[#F1FBF4] flex-1 p-2 space-y-2 rounded-md">
        <p className="text-sm text-brand font-medium">Today</p>
        <p className="text-[#575757] text-[10px] font-light">
          {estimatedDailyDistance ? `${estimatedDailyDistance}km` : "-"}
        </p>
      </div>
    </div>
  );
};

export default Home;
